import React, { useEffect } from 'react';
import { FiAlertTriangle, FiInbox, FiInfo, FiCheckCircle, FiXCircle, FiCalendar } from 'react-icons/fi';

export const PageHeader: React.FC<{ title: string; description?: React.ReactNode; actions?: React.ReactNode }> = ({
  title,
  description,
  actions,
}) => (
  <div className="page-header">
    <div>
      <h1>{title}</h1>
      {description && <p>{description}</p>}
    </div>
    {actions && <div className="page-header__actions">{actions}</div>}
  </div>
);

type KpiProps = {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon?: React.ReactNode;
  tone?: 'navy' | 'coral' | 'green' | 'amber';
  loading?: boolean;
  /** Makes the card a button, e.g. to filter the list below to the people it counts. */
  onClick?: () => void;
  active?: boolean;
  title?: string;
};

export const Kpi: React.FC<KpiProps> = ({ label, value, sub, icon, tone = 'navy', loading, onClick, active, title }) => {
  const body = (
    <>
      <div className="kpi__label">
        {icon && <span className={`kpi__icon${tone !== 'navy' ? ` kpi__icon--${tone}` : ''}`}>{icon}</span>}
        {label}
      </div>
      <div className="kpi__value">{loading ? <Skeleton width="9rem" height="3rem" /> : value}</div>
      {sub !== undefined && <div className="kpi__sub">{loading ? <Skeleton width="14rem" /> : sub}</div>}
    </>
  );
  return onClick ? (
    <button type="button" className={`kpi kpi--button${active ? ' is-active' : ''}`} onClick={onClick} aria-pressed={!!active} title={title || `Show ${label.toLowerCase()}`}>
      {body}
    </button>
  ) : (
    <div className="kpi">{body}</div>
  );
};

export const Skeleton: React.FC<{ width?: string; height?: string; circle?: boolean; block?: boolean }> = ({
  width = '100%',
  height = '1.2rem',
  circle,
  block,
}) => (
  <span
    className={`skeleton${circle ? ' skeleton--circle' : ''}${block ? ' skeleton--block' : ''}`}
    style={{ width, height }}
    aria-hidden="true"
  />
);

export const Badge: React.FC<{ tone?: string; plain?: boolean }> = ({ tone = 'free', plain, children }) => (
  <span className={`badge badge--${tone}${plain ? ' badge--plain' : ''}`}>{children}</span>
);

export const EmptyState: React.FC<{ title: string; icon?: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="empty-state">
    {icon || <FiInbox />}
    <strong>{title}</strong>
    {children && <div>{children}</div>}
  </div>
);

const NOTICE_ICONS = {
  warning: <FiAlertTriangle />,
  info: <FiInfo />,
  error: <FiXCircle />,
  success: <FiCheckCircle />,
};

export const Notice: React.FC<{ tone?: 'warning' | 'info' | 'error' | 'success'; style?: React.CSSProperties }> = ({
  tone = 'warning',
  style,
  children,
}) => (
  <div className={`notice${tone !== 'warning' ? ` notice--${tone}` : ''}`} role={tone === 'error' ? 'alert' : 'status'} style={style}>
    {NOTICE_ICONS[tone]}
    <div>{children}</div>
  </div>
);

type ModalProps = {
  title: string;
  onClose: () => void;
  actions?: React.ReactNode;
};

export const Modal: React.FC<ModalProps> = ({ title, onClose, actions, children }) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="ui-modal" role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        {children}
        {actions && <div className="ui-modal__actions">{actions}</div>}
      </div>
    </>
  );
};

export const DateRange: React.FC<{
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
  disabled?: boolean;
  label?: string;
}> = ({ from, to, onChange, disabled, label = 'Signed up' }) => (
  <div className="date-range" title={`${label} between these dates`}>
    <FiCalendar />
    <span>{label}</span>
    <input type="date" aria-label={`${label} from`} value={from} max={to || undefined} disabled={disabled} onChange={(event) => onChange({ from: event.target.value, to })} />
    <span aria-hidden="true">–</span>
    <input type="date" aria-label={`${label} to`} value={to} min={from || undefined} disabled={disabled} onChange={(event) => onChange({ from, to: event.target.value })} />
  </div>
);
